/**
 * tela.ts — a única resposta para "isto é celular?".
 *
 * Escrito em 25/08/2026. Antes disto o número `992` estava cravado em TRÊS
 * arquivos (`CustomMainHeader`, `CustomSidebar`, `InternalLayout`) e só um deles
 * tinha nome. Os três concordavam no valor — por sorte, não por decisão: mudar
 * o corte exigia achar três lugares, e dois não se achavam buscando por nome.
 *
 * A convenção visual (§8.3) manda isto morar num lugar só, e é aqui.
 */
import { useEffect, useState } from 'react';

/**
 * Abaixo disto o menu lateral some e a tela é um celular.
 *
 * É o `lg` do Bootstrap, e não um número nosso: o app já usa essa escala em
 * ~430 lugares, e inventar um quarto corte é como nasceram as três cópias do
 * 992. Ver a seção 8 de `11-convencao-visual.md`.
 */
export const LARGURA_DESKTOP = 992;

/** A consulta, escrita uma vez. */
const CONSULTA = `(max-width: ${LARGURA_DESKTOP - 1}px)`;

/**
 * `true` quando a tela é de celular — e ele ACOMPANHA a mudança de tamanho.
 *
 * Ler `window.innerWidth` uma vez responde certo e depois mente: quem gira o
 * aparelho, abre o teclado ou arrasta a janela fica com a resposta velha. O
 * `matchMedia` avisa, e é o mesmo mecanismo que o CSS usa — então a tela e a
 * folha de estilo nunca discordam.
 *
 * ⚠️ Trocar de arranjo troca o componente, e componente novo nasce sem o que a
 * pessoa digitou. Numa tela com formulário, o estado tem de viver ACIMA de quem
 * escolhe — nunca dentro de cada arranjo.
 */
export function usarCelular(): boolean {
  const [celular, setCelular] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(CONSULTA).matches,
  );

  useEffect(() => {
    const mq = window.matchMedia(CONSULTA);
    const aoMudar = (e: MediaQueryListEvent) => setCelular(e.matches);
    mq.addEventListener('change', aoMudar);
    setCelular(mq.matches); // caso tenha mudado entre o primeiro render e agora
    return () => mq.removeEventListener('change', aoMudar);
  }, []);

  return celular;
}

/**
 * A mesma pergunta fora de um componente React — para quem precisa decidir numa
 * função solta (fechar o menu ao clicar, por exemplo). Não acompanha mudança:
 * responde sobre o instante em que foi chamada.
 */
export function ehCelularAgora(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(CONSULTA).matches;
}
